import { useCallback, useEffect, useState } from "react";
import { Spinner } from "@fluentui/react-components";

import { DocumentFingerprint, Documento, SharePointInfo } from "../types";
import {
  readCustomProperties,
  getSharePointInfo,
  oliDocClient,
} from "../services";

import { DocumentContext } from "./components/DocumentContext";
import { UnboundDocument } from "./components/UnboundDocument";
import { ExternalDocument } from "./components/ExternalDocument";
import { ErrorMessage } from "./components/ErrorMessage";

import "./App.css";

// Application state
type AppState =
  | { status: "loading" }
  | { status: "bound"; documento: Documento }
  | { status: "unbound"; fingerprint: DocumentFingerprint; message: string }
  | { status: "external"; sharePointInfo: SharePointInfo }
  | { status: "error"; message: string };

export function App() {
  const [state, setState] = useState<AppState>({ status: "loading" });

  const initializeAddin = useCallback(async () => {
    try {
      console.log("=== OLI Word Add-in ===");

      // 1. Read custom properties
      const fingerprint = await readCustomProperties();
      console.log("Fingerprint:", fingerprint);

      // 2. Try meta_document_id (best case)
      if (fingerprint?.meta_document_id) {
        console.log(
          "Looking up by meta_document_id:",
          fingerprint.meta_document_id
        );

        const documento = await oliDocClient.getDocumentoById(
          fingerprint.meta_document_id
        );

        if (documento) {
          setState({ status: "bound", documento });
          return;
        }
      }

      // 3. Fallback: try file_item_id
      if (fingerprint?.file_item_id) {
        console.log("Looking up by file_item_id:", fingerprint.file_item_id);

        const lookup = await oliDocClient.getDocumentoByItemId(
          fingerprint.file_item_id
        );

        if (lookup.bound && lookup.documento) {
          setState({ status: "bound", documento: lookup.documento });
        } else {
          setState({
            status: "unbound",
            fingerprint,
            message: lookup.message || "Documento não vinculado",
          });
        }
        return;
      }

      // 4. External document (no fingerprint)
      const sharePointInfo = getSharePointInfo();
      console.log("SharePoint info:", sharePointInfo);

      setState({ status: "external", sharePointInfo });
    } catch (error) {
      console.error("Error initializing:", error);
      setState({
        status: "error",
        message: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (!cancelled) {
        await initializeAddin();
      }
    };

    void boot();

    return () => {
      cancelled = true;
    };
  }, [initializeAddin]);

  function handleDocumentBound(documento: Documento) {
    setState({ status: "bound", documento });
  }

  function handleRetry() {
    setState({ status: "loading" });
    void initializeAddin();
  }

  // Render based on state
  switch (state.status) {
    case "loading":
      return (
        <div className="app-loading">
          <Spinner size="medium" label="Carregando documento..." />
        </div>
      );

    case "bound":
      return (
        <DocumentContext
          documento={state.documento}
          onStatusChange={handleDocumentBound}
        />
      );

    case "unbound":
      return (
        <UnboundDocument
          fingerprint={state.fingerprint}
          message={state.message}
          onBind={handleDocumentBound}
        />
      );

    case "external":
    case "error":
      return <DocumentContext />;

    default: {
      const _exhaustive: never = state;
      return null;
    }
  }
}
